import { useEffect, useRef, useState } from "react";

import {
  agentSessionStatePayloadSchema,
  iceCandidateSchema,
  protocolVersion,
  realtimeEnvelopeSchema,
  sessionDescriptionSchema,
  sessionResponseSchema,
  type ControlMessage,
} from "@remote-android/contracts";
import { io, type Socket } from "socket.io-client";

interface SignalAcknowledgement {
  readonly ok: boolean;
  readonly code?: string;
}

interface BrowserListenEvents {
  "session:state": (message: unknown) => void;
  "session:error": (message: unknown) => void;
  "webrtc:offer": (message: unknown) => void;
  "webrtc:answer": (message: unknown) => void;
  "webrtc:ice-candidate": (message: unknown) => void;
  "controller:revoked": (message: unknown) => void;
}

interface BrowserEmitEvents {
  "session:join": (
    message: unknown,
    acknowledgement: (result: SignalAcknowledgement) => void,
  ) => void;
  "session:leave": (
    message: unknown,
    acknowledgement: (result: SignalAcknowledgement) => void,
  ) => void;
  "session:heartbeat": (
    message: unknown,
    acknowledgement: (result: SignalAcknowledgement) => void,
  ) => void;
  "webrtc:answer": (
    message: unknown,
    acknowledgement: (result: SignalAcknowledgement) => void,
  ) => void;
  "webrtc:ice-candidate": (
    message: unknown,
    acknowledgement: (result: SignalAcknowledgement) => void,
  ) => void;
}

export interface WebRtcViewerProps {
  readonly accessToken: string;
  readonly deviceId: string;
  readonly mode: "view" | "control";
  readonly forceRelay: boolean;
}

interface NormalizedPoint {
  readonly x: number;
  readonly y: number;
}

type ControlKey = Extract<ControlMessage, { type: "key" }>["key"];

function mapKeyboardKey(key: string): ControlKey | null {
  switch (key) {
    case "Enter":
      return "enter";
    case "Backspace":
      return "backspace";
    case "Delete":
      return "delete";
    case "Tab":
      return "tab";
    case "Escape":
      return "escape";
    case "ArrowUp":
      return "arrowUp";
    case "ArrowDown":
      return "arrowDown";
    case "ArrowLeft":
      return "arrowLeft";
    case "ArrowRight":
      return "arrowRight";
    default:
      return null;
  }
}

function createEnvelope(sessionId: string, payload: unknown) {
  return {
    protocolVersion,
    eventId: crypto.randomUUID(),
    timestamp: new Date().toISOString(),
    correlationId: crypto.randomUUID(),
    sessionId,
    payload,
  };
}

function csrfResponseError(response: Response): Error {
  return new Error(`Session request failed with HTTP ${response.status}`);
}

function normalizedVideoPoint(
  video: HTMLVideoElement,
  clientX: number,
  clientY: number,
): NormalizedPoint | null {
  const bounds = video.getBoundingClientRect();
  if (video.videoWidth <= 0 || video.videoHeight <= 0 || bounds.width <= 0 || bounds.height <= 0) {
    return null;
  }
  const scale = Math.min(bounds.width / video.videoWidth, bounds.height / video.videoHeight);
  const renderedWidth = video.videoWidth * scale;
  const renderedHeight = video.videoHeight * scale;
  const offsetX = (bounds.width - renderedWidth) / 2;
  const offsetY = (bounds.height - renderedHeight) / 2;
  const x = (clientX - bounds.left - offsetX) / renderedWidth;
  const y = (clientY - bounds.top - offsetY) / renderedHeight;
  if (x < 0 || x > 1 || y < 0 || y > 1) {
    return null;
  }
  return { x, y };
}

export function WebRtcViewer({ accessToken, deviceId, mode, forceRelay }: WebRtcViewerProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const viewerContainerRef = useRef<HTMLDivElement>(null);
  const dataChannelRef = useRef<RTCDataChannel | null>(null);
  const pointerChannelRef = useRef<RTCDataChannel | null>(null);
  const pendingPointerMoveRef = useRef<Extract<ControlMessage, { type: "pointer" }> | null>(null);
  const pointerAnimationFrameRef = useRef<number | null>(null);
  const lastPointerMoveSentAtRef = useRef(0);
  const lastPointerPointRef = useRef<NormalizedPoint | null>(null);
  const sessionNonceRef = useRef<string | null>(null);
  const [status, setStatus] = useState("Meminta sesi...");
  const [error, setError] = useState<string | null>(null);
  const [connectionMode, setConnectionMode] = useState<"UNKNOWN" | "P2P" | "TURN">("UNKNOWN");
  const [controlReady, setControlReady] = useState(false);
  const [lastControlResult, setLastControlResult] = useState<string | null>(null);
  const [keyboardFocused, setKeyboardFocused] = useState(false);
  const [sessionStartedAt, setSessionStartedAt] = useState<number | null>(null);

  useEffect(() => {
    let disposed = false;
    let sessionId: string | null = null;
    let socket: Socket<BrowserListenEvents, BrowserEmitEvents> | null = null;
    let peer: RTCPeerConnection | null = null;
    let heartbeatTimer: number | null = null;
    let statsTimer: number | null = null;
    let remoteDescriptionReady = false;
    const pendingRemoteCandidates: RTCIceCandidateInit[] = [];

    async function emitAcknowledged(
      event: keyof BrowserEmitEvents,
      message: unknown,
    ): Promise<boolean> {
      if (!socket) {
        return false;
      }
      return new Promise((resolve) => {
        const timeout = window.setTimeout(() => resolve(false), 10_000);
        const callback = (result: SignalAcknowledgement) => {
          window.clearTimeout(timeout);
          resolve(result.ok);
        };
        if (event === "session:join") {
          socket?.emit(event, message, callback);
        } else if (event === "session:leave") {
          socket?.emit(event, message, callback);
        } else if (event === "session:heartbeat") {
          socket?.emit(event, message, callback);
        } else if (event === "webrtc:answer") {
          socket?.emit(event, message, callback);
        } else {
          socket?.emit("webrtc:ice-candidate", message, callback);
        }
      });
    }

    async function updateConnectionMode(): Promise<void> {
      if (peer?.connectionState !== "connected") {
        return;
      }
      const stats = await peer.getStats();
      const selectedPairs: RTCIceCandidatePairStats[] = [];
      stats.forEach((report: unknown) => {
        if (
          typeof report === "object" &&
          report !== null &&
          "type" in report &&
          report.type === "candidate-pair" &&
          "state" in report &&
          report.state === "succeeded" &&
          "nominated" in report &&
          report.nominated === true
        ) {
          selectedPairs.push(report as RTCIceCandidatePairStats);
        }
      });
      const selectedPair = selectedPairs[0];
      if (!selectedPair) {
        return;
      }
      const remote = stats.get(selectedPair.remoteCandidateId) as unknown;
      const relayed =
        typeof remote === "object" &&
        remote !== null &&
        "candidateType" in remote &&
        remote.candidateType === "relay";
      setConnectionMode(relayed ? "TURN" : "P2P");
    }

    async function start(): Promise<void> {
      const sessionResponse = await fetch(`/api/v1/devices/${deviceId}/session`, {
        method: "POST",
        credentials: "include",
        headers: {
          authorization: `Bearer ${accessToken}`,
          "content-type": "application/json",
        },
        body: JSON.stringify({
          mode: mode === "control" ? "CONTROL" : "VIEW",
          forceRelay,
        }),
      });
      if (!sessionResponse.ok) {
        throw csrfResponseError(sessionResponse);
      }
      const session = sessionResponseSchema.parse((await sessionResponse.json()) as unknown);
      sessionId = session.id;
      sessionNonceRef.current = session.signalingNonce;

      const iceResponse = await fetch(`/api/v1/sessions/${session.id}/ice-servers`, {
        credentials: "include",
        headers: { authorization: `Bearer ${accessToken}` },
      });
      if (!iceResponse.ok) {
        throw csrfResponseError(iceResponse);
      }
      const iceBody = (await iceResponse.json()) as {
        iceServers?: RTCIceServer[];
      };
      peer = new RTCPeerConnection({
        iceServers: iceBody.iceServers ?? [],
        iceTransportPolicy: forceRelay ? "relay" : "all",
      });
      peer.ontrack = (event) => {
        if (videoRef.current && event.streams[0]) {
          videoRef.current.srcObject = event.streams[0];
          void videoRef.current.play();
        }
      };
      peer.ondatachannel = (event) => {
        const isControlChannel = event.channel.label === "control";
        const isPointerChannel = event.channel.label === "pointer";
        if (!isControlChannel && !isPointerChannel) {
          event.channel.close();
          return;
        }
        if (isControlChannel) {
          dataChannelRef.current = event.channel;
        } else {
          pointerChannelRef.current = event.channel;
        }
        const authorizeControl = () => {
          const nonce = sessionNonceRef.current;
          if (nonce) {
            event.channel.send(
              JSON.stringify({ type: "authorize", nonce } satisfies ControlMessage),
            );
          }
        };
        event.channel.onopen = authorizeControl;
        event.channel.onmessage = (messageEvent) => {
          try {
            const message = JSON.parse(String(messageEvent.data)) as unknown;
            if (
              typeof message === "object" &&
              message !== null &&
              "type" in message &&
              message.type === "authorized" &&
              "ok" in message
            ) {
              if (isControlChannel) {
                setControlReady(message.ok === true);
              }
            } else if (
              typeof message === "object" &&
              message !== null &&
              "type" in message &&
              message.type === "control-result" &&
              "command" in message &&
              typeof message.command === "string" &&
              "ok" in message
            ) {
              setLastControlResult(
                message.ok === true ? `${message.command}: OK` : `${message.command}: FAILED`,
              );
            }
          } catch {
            // Ignore unknown Agent DataChannel messages.
          }
        };
        event.channel.onclose = () => {
          if (isControlChannel) {
            dataChannelRef.current = null;
            setControlReady(false);
          } else {
            pointerChannelRef.current = null;
          }
        };
        if (event.channel.readyState === "open") {
          authorizeControl();
        }
      };
      peer.onicecandidate = (event) => {
        if (event.candidate && sessionId) {
          void emitAcknowledged(
            "webrtc:ice-candidate",
            createEnvelope(sessionId, event.candidate.toJSON()),
          );
        }
      };
      peer.onconnectionstatechange = () => {
        if (!peer) {
          return;
        }
        setStatus(peer.connectionState);
        if (peer.connectionState === "connected") {
          setSessionStartedAt((current) => current ?? Date.now());
          void updateConnectionMode();
        }
        if (peer.connectionState === "failed") {
          setError("Koneksi WebRTC gagal.");
        }
      };

      socket = io({
        path: "/socket.io",
        transports: ["websocket"],
        auth: { accessToken },
        reconnection: true,
      });
      socket.on("webrtc:offer", (message) => {
        void (async () => {
          if (!peer || !sessionId) {
            return;
          }
          const envelope = realtimeEnvelopeSchema.safeParse(message);
          const description = sessionDescriptionSchema.safeParse(
            envelope.success ? envelope.data.payload : null,
          );
          if (
            !envelope.success ||
            envelope.data.sessionId !== sessionId ||
            !description.success ||
            description.data.type !== "offer"
          ) {
            return;
          }
          await peer.setRemoteDescription(description.data);
          remoteDescriptionReady = true;
          for (const candidate of pendingRemoteCandidates.splice(0)) {
            await peer.addIceCandidate(candidate);
          }
          const answer = await peer.createAnswer();
          await peer.setLocalDescription(answer);
          await emitAcknowledged("webrtc:answer", createEnvelope(sessionId, answer));
        })().catch(() => setError("Negosiasi WebRTC gagal."));
      });
      socket.on("webrtc:ice-candidate", (message) => {
        void (async () => {
          if (!peer || !sessionId) {
            return;
          }
          const envelope = realtimeEnvelopeSchema.safeParse(message);
          const candidate = iceCandidateSchema.safeParse(
            envelope.success ? envelope.data.payload : null,
          );
          if (!envelope.success || envelope.data.sessionId !== sessionId || !candidate.success) {
            return;
          }
          const init: RTCIceCandidateInit = {
            candidate: candidate.data.candidate,
            sdpMid: candidate.data.sdpMid,
            sdpMLineIndex: candidate.data.sdpMLineIndex,
          };
          if (remoteDescriptionReady) {
            await peer.addIceCandidate(init);
          } else {
            pendingRemoteCandidates.push(init);
          }
        })().catch(() => setError("ICE candidate ditolak."));
      });
      socket.on("session:state", (message) => {
        const envelope = realtimeEnvelopeSchema.safeParse(message);
        const state = agentSessionStatePayloadSchema.safeParse(
          envelope.success ? envelope.data.payload : null,
        );
        if (state.success && state.data.sessionId === sessionId) {
          setStatus(state.data.state);
          if (state.data.state === "FAILED") {
            setError(`Agent gagal: ${state.data.errorCode ?? "UNKNOWN"}`);
          }
        }
      });
      socket.on("controller:revoked", () => {
        setError("Hak kontrol sesi telah dicabut.");
      });

      await new Promise<void>((resolve, reject) => {
        socket?.once("connect", resolve);
        socket?.once("connect_error", reject);
      });
      setStatus("Menghubungkan Agent...");
      const joined = await emitAcknowledged(
        "session:join",
        createEnvelope(session.id, { nonce: session.signalingNonce }),
      );
      if (!joined) {
        throw new Error("Agent tidak tersedia untuk sesi ini.");
      }
      heartbeatTimer = window.setInterval(() => {
        if (sessionId) {
          void emitAcknowledged("session:heartbeat", createEnvelope(sessionId, {}));
        }
      }, 10_000);
      statsTimer = window.setInterval(() => {
        void updateConnectionMode();
      }, 2_000);
    }

    void start().catch((caught: unknown) => {
      if (!disposed) {
        setError(caught instanceof Error ? caught.message : "Sesi tidak dapat dimulai.");
        setStatus("FAILED");
      }
    });

    return () => {
      disposed = true;
      if (heartbeatTimer !== null) {
        window.clearInterval(heartbeatTimer);
      }
      if (statsTimer !== null) {
        window.clearInterval(statsTimer);
      }
      if (socket && sessionId) {
        void emitAcknowledged("session:leave", createEnvelope(sessionId, {}));
      }
      socket?.disconnect();
      peer?.close();
      dataChannelRef.current = null;
      pointerChannelRef.current = null;
      pendingPointerMoveRef.current = null;
      lastPointerPointRef.current = null;
      if (pointerAnimationFrameRef.current !== null) {
        window.cancelAnimationFrame(pointerAnimationFrameRef.current);
        pointerAnimationFrameRef.current = null;
      }
      setControlReady(false);
      setLastControlResult(null);
      if (sessionId) {
        void fetch(`/api/v1/sessions/${sessionId}`, {
          method: "DELETE",
          credentials: "include",
          keepalive: true,
          headers: { authorization: `Bearer ${accessToken}` },
        });
      }
    };
  }, [accessToken, deviceId, forceRelay, mode]);

  function sendControl(message: ControlMessage): void {
    const channel =
      message.type === "pointer" && message.action === "move"
        ? (pointerChannelRef.current ?? dataChannelRef.current)
        : dataChannelRef.current;
    if (controlReady && channel?.readyState === "open") {
      channel.send(JSON.stringify(message));
      if (message.type !== "pointer" || message.action === "down" || message.action === "up") {
        setLastControlResult(`${message.type}: sent`);
      }
    }
  }

  function schedulePointerMove(message: Extract<ControlMessage, { type: "pointer" }>): void {
    pendingPointerMoveRef.current = message;
    if (pointerAnimationFrameRef.current !== null) {
      return;
    }
    const flush = (timestamp: number) => {
      if (timestamp - lastPointerMoveSentAtRef.current < 1_000 / 30) {
        pointerAnimationFrameRef.current = window.requestAnimationFrame(flush);
        return;
      }
      pointerAnimationFrameRef.current = null;
      const pending = pendingPointerMoveRef.current;
      pendingPointerMoveRef.current = null;
      if (pending) {
        lastPointerMoveSentAtRef.current = timestamp;
        sendControl(pending);
      }
    };
    pointerAnimationFrameRef.current = window.requestAnimationFrame(flush);
  }

  const readOnly = mode !== "control";

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center gap-2 text-xs">
        <span className="rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-slate-300">
          {status}
        </span>
        <span className="rounded-full border border-violet-400/20 bg-violet-400/10 px-3 py-1.5 text-violet-300">
          {connectionMode}
        </span>
        {forceRelay ? (
          <span className="rounded-full border border-amber-400/20 bg-amber-400/10 px-3 py-1.5 text-amber-300">
            Forced TURN
          </span>
        ) : null}
        {mode === "control" ? (
          <span
            className={`rounded-full border px-3 py-1.5 ${
              controlReady
                ? "border-emerald-400/20 bg-emerald-400/10 text-emerald-300"
                : "border-amber-400/20 bg-amber-400/10 text-amber-300"
            }`}
          >
            {controlReady ? "Control ready" : "Control connecting"}
          </span>
        ) : null}
        {lastControlResult ? (
          <span className="text-slate-500">Input {lastControlResult}</span>
        ) : null}
        {mode === "control" ? (
          <span className={keyboardFocused ? "text-emerald-400" : "text-slate-500"}>
            {keyboardFocused ? "Keyboard aktif" : "Klik layar untuk aktifkan keyboard"}
          </span>
        ) : null}
        {sessionStartedAt ? (
          <span className="text-slate-500">
            Sesi dimulai {new Date(sessionStartedAt).toLocaleTimeString()}
          </span>
        ) : null}
      </div>

      {error ? (
        <div className="mb-3 rounded-xl border border-rose-400/20 bg-rose-400/10 px-4 py-3 text-sm text-rose-300">
          {error}
        </div>
      ) : null}

      <div
        className="relative grid min-h-[65vh] place-items-center overflow-hidden rounded-3xl border border-white/8 bg-black/80 shadow-2xl outline-none"
        onBlur={() => setKeyboardFocused(false)}
        onFocus={() => setKeyboardFocused(true)}
        onKeyDown={(event) => {
          if (readOnly || event.ctrlKey || event.altKey || event.metaKey) {
            return;
          }
          const mappedKey = mapKeyboardKey(event.key);
          if (mappedKey) {
            event.preventDefault();
            sendControl({ type: "key", key: mappedKey });
            return;
          }
          if (event.key.length === 1) {
            event.preventDefault();
            sendControl({ type: "text", text: event.key });
          }
        }}
        ref={viewerContainerRef}
        role="application"
        tabIndex={0}
      >
        <video
          autoPlay
          className="h-[65vh] w-full cursor-crosshair touch-none object-contain select-none"
          muted
          onPointerDown={(event) => {
            viewerContainerRef.current?.focus();
            if (readOnly || !videoRef.current) {
              return;
            }
            event.preventDefault();
            const point = normalizedVideoPoint(videoRef.current, event.clientX, event.clientY);
            if (point) {
              lastPointerPointRef.current = point;
              event.currentTarget.setPointerCapture(event.pointerId);
              sendControl({
                type: "pointer",
                action: "down",
                ...point,
                pointerId: 0,
              });
            }
          }}
          onPointerMove={(event) => {
            if (
              readOnly ||
              !videoRef.current ||
              !event.currentTarget.hasPointerCapture(event.pointerId)
            ) {
              return;
            }
            event.preventDefault();
            const point = normalizedVideoPoint(videoRef.current, event.clientX, event.clientY);
            if (point) {
              lastPointerPointRef.current = point;
              schedulePointerMove({
                type: "pointer",
                action: "move",
                ...point,
                pointerId: 0,
              });
            }
          }}
          onPointerUp={(event) => {
            if (readOnly || !videoRef.current) {
              return;
            }
            event.preventDefault();
            pendingPointerMoveRef.current = null;
            if (pointerAnimationFrameRef.current !== null) {
              window.cancelAnimationFrame(pointerAnimationFrameRef.current);
              pointerAnimationFrameRef.current = null;
            }
            const point =
              normalizedVideoPoint(videoRef.current, event.clientX, event.clientY) ??
              lastPointerPointRef.current;
            if (point) {
              sendControl({
                type: "pointer",
                action: "up",
                ...point,
                pointerId: 0,
              });
            }
            lastPointerPointRef.current = null;
            if (event.currentTarget.hasPointerCapture(event.pointerId)) {
              event.currentTarget.releasePointerCapture(event.pointerId);
            }
          }}
          onPointerCancel={(event) => {
            if (
              readOnly ||
              !videoRef.current ||
              !event.currentTarget.hasPointerCapture(event.pointerId)
            ) {
              return;
            }
            event.preventDefault();
            pendingPointerMoveRef.current = null;
            if (pointerAnimationFrameRef.current !== null) {
              window.cancelAnimationFrame(pointerAnimationFrameRef.current);
              pointerAnimationFrameRef.current = null;
            }
            const point =
              normalizedVideoPoint(videoRef.current, event.clientX, event.clientY) ??
              lastPointerPointRef.current;
            if (point) {
              sendControl({
                type: "pointer",
                action: "up",
                ...point,
                pointerId: 0,
              });
            }
            lastPointerPointRef.current = null;
            if (event.currentTarget.hasPointerCapture(event.pointerId)) {
              event.currentTarget.releasePointerCapture(event.pointerId);
            }
          }}
          playsInline
          ref={videoRef}
        />
      </div>

      <div className="mt-3 flex flex-wrap gap-2">
        {(["back", "home", "appSwitch"] as const).map((key) => (
          <button
            className="rounded-lg border border-white/10 bg-white/5 px-4 py-2 text-xs font-semibold text-slate-300 disabled:opacity-40"
            disabled={readOnly || !controlReady}
            key={key}
            onClick={() => sendControl({ type: "key", key })}
            type="button"
          >
            {key === "appSwitch" ? "App switch" : key}
          </button>
        ))}
        <button
          className="rounded-lg border border-white/10 bg-white/5 px-4 py-2 text-xs font-semibold text-slate-300 disabled:opacity-40"
          disabled={readOnly || !controlReady}
          onClick={() => sendControl({ type: "rotate" })}
          type="button"
        >
          Rotate
        </button>
      </div>
    </div>
  );
}
